import React, { useCallback } from 'react';
import { Tooltip } from '@mui/material';

import stopTypes, { pickupKindTypes } from '../../../enums/stopTypes';
import {
  ignoreServiceException,
  isRouteStatusInBuild,
  isServiceException,
  totalRouteExceptions,
} from '../../../models/routeDetail';
import {
  formatSecondsToHHMM,
  formatStopTime,
  formatTimeStringWithDefaultMaxValue,
  reformatTime,
} from '../../../utils/date';
import {
  formatToKg,
  getExceptionEntry,
  getRagStatusColor,
  countTotalByField,
  isPendingStop,
  getStopRequestType,
  getRagStatusTextColor,
  getStatusTextColor,
  getAPRequests,
} from '../../../features/routeDetail/model';
import {
  ADDRESS,
  CAD,
  CHUTE,
  ETA,
  EXCEPTION,
  LOADED,
  OT,
  PARCELS,
  POSTCODE,
  PRIORITY,
  RAG,
  REQUEST,
  SMALLS,
  STOP,
  STOP_TIME,
  TYPE,
  WEIGHT,
  WINDOW,
  RETURN_TO_DEPOT,
  NON_COMPATIBLES_SHORT,
  MANIFEST_LOCATION,
} from '../../../constants/strings';
import { COLUMN_ACCESSORS as CA } from '../../../constants/table';
import BodyCell from '../../common/Table/BodyCell';
import HeaderCell from '../../common/Table/HeaderCell';
import {
  Add,
  Consolidated,
  Remove,
  SpotIcon,
  Defer,
  MixedAddRemove,
} from '../../icons';
import {
  HighValueItem,
  ManifestLocationItem,
  ServiceItem,
} from '../../common/Table/Items';
import StopInfoItem from '../../common/Table/StopInfoItem';
import EditableCell from '../../common/Table/EditableCell';
import { requestIconType } from '../../../enums/requestTypes';
import WarningWithTooltip from '../../WarningWithTooltip';
import ShopPickupIcon from '../../icons/ShopPickupIcon';
import LockerPickupIcon from '../../icons/LockerPickupIcon';
import { ConditionalWrapper } from '../../ConditionalWrapper';

const stopListColumns = ({
  route,
  modifiers,
  columnsConfigs,
  headerCellClasses,
  bodyCellClasses,
}) => [
  {
    Cell: ({ rowData }) => (
      <HighValueItem rowData={rowData} classes={bodyCellClasses} />
    ),
    columnConfig: columnsConfigs[CA.HIGH_VALUE],
    accessor: CA.HIGH_VALUE,
  },
  {
    Cell: ({ value }) => (
      <BodyCell
        value={<ServiceItem value={value} />}
        classes={bodyCellClasses}
        extraCellClasses={[bodyCellClasses.cellTextBold]}
        testId={`${CA.SERVICE}-icon-${getStatusTextColor(value)}`}
      />
    ),
    columnConfig: columnsConfigs[CA.SERVICE],
    accessor: CA.SERVICE,
  },
  {
    Cell: ({ value }) => {
      let displayContent = null;

      switch (value) {
        case pickupKindTypes.shop:
          displayContent = <ShopPickupIcon />;
          break;
        case pickupKindTypes.locker:
          displayContent = <LockerPickupIcon />;
          break;
        default:
          displayContent = null;
      }

      return (
        <BodyCell
          value={displayContent}
          classes={bodyCellClasses}
          extraContainerClasses={[bodyCellClasses.iconCell]}
          extraCellClasses={[bodyCellClasses.lastIcon]}
          testId={`${CA.PICKUP_KIND}-icon-${value}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.PICKUP_KIND],
    accessor: CA.PICKUP_KIND,
  },
  {
    Cell: ({ value, rowData }) => {
      const isBreakStop = rowData.type === stopTypes.break;
      const bodyProps = {};

      const onClick = useCallback((e) => {
        e.stopPropagation();
        modifiers.onParcelInfoClick(value);
      });
      if (!isBreakStop && value) {
        bodyProps.onClick = onClick;
        bodyProps.value = <StopInfoItem />;
        bodyProps.testId = `${CA.PARCELS_INFO}-icon`;
      }
      return (
        <BodyCell
          classes={bodyCellClasses}
          extraContainerClasses={[bodyCellClasses.iconCell]}
          extraCellClasses={[bodyCellClasses.lastIcon]}
          {...bodyProps}
        />
      );
    },
    columnConfig: columnsConfigs[CA.PARCELS_INFO],
    accessor: CA.PARCELS_INFO,
  },
  {
    Header: ({ data }) => (
      <HeaderCell
        header={STOP}
        subheader={
          columnsConfigs[CA.DISPLAY_NUMBER]?.isSubheader && data.length
        }
        classes={headerCellClasses}
      />
    ),
    Cell: ({ value, rowData }) => {
      const data = rowData.type === stopTypes.break ? '' : value;
      return <BodyCell value={data} classes={bodyCellClasses} />;
    },
    columnConfig: columnsConfigs[CA.DISPLAY_NUMBER],
    accessor: CA.DISPLAY_NUMBER,
    sortByAccessor: CA.SEQUENCE_NUMBER,
  },
  {
    Header: () => <HeaderCell header={PRIORITY} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.PRIORITY],
    accessor: CA.PRIORITY,
  },
  {
    Header: () => <HeaderCell header={TYPE} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.TYPE],
    accessor: CA.TYPE,
  },
  {
    Header: () => <HeaderCell header={ADDRESS} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell
        value={value}
        classes={bodyCellClasses}
        extraCellClasses={[bodyCellClasses.cellCroppedText]}
      />
    ),
    Footer: () => (
      <BodyCell
        value={RETURN_TO_DEPOT.toUpperCase()}
        classes={bodyCellClasses}
        extraCellClasses={
          route?.returnToDepotLegalException ? [bodyCellClasses.redText] : []
        }
        testId={'tr-footer'}
      />
    ),
    columnConfig: columnsConfigs[CA.ADDRESS],
    accessor: CA.ADDRESS,
  },
  {
    Header: () => (
      <HeaderCell header={MANIFEST_LOCATION} classes={headerCellClasses} />
    ),
    Cell: ({ value }) => (
      <ManifestLocationItem
        value={value}
        classes={bodyCellClasses}
        maxCellValueWidth={13}
      />
    ),
    columnConfig: columnsConfigs[CA.MANIFEST_LOCATION],
    accessor: CA.MANIFEST_LOCATION,
  },
  {
    Header: () => <HeaderCell header={POSTCODE} classes={headerCellClasses} />,
    Cell: ({ value = '', rowData }) => {
      const isBreakStop = rowData.type === stopTypes.break;
      const isPending = isPendingStop(rowData);
      const isAPRequest = !!getAPRequests(rowData)?.length;
      const isEditable = !(
        !modifiers ||
        isPending ||
        isRouteStatusInBuild(route) ||
        isAPRequest
      );
      const commonProps = {
        value,
        classes: bodyCellClasses,
      };
      return !isEditable || isBreakStop ? (
        <ConditionalWrapper
          condition={isAPRequest}
          wrapper={(children) => (
            <Tooltip
              placement="top"
              fontSize="small"
              title={`Stop has open request(s)`}
              arrow
            >
              {children}
            </Tooltip>
          )}
        >
          <span>
            <BodyCell {...commonProps} />
          </span>
        </ConditionalWrapper>
      ) : (
        <EditableCell
          {...commonProps}
          rowData={rowData}
          onSubmit={modifiers?.handleStopPostcodeChange}
        />
      );
    },
    columnConfig: columnsConfigs[CA.POSTCODE],
    accessor: CA.POSTCODE,
  },
  {
    Header: () => <HeaderCell header={RAG} classes={headerCellClasses} />,
    Cell: ({ value }) => {
      const icon = !!value && <SpotIcon color={getRagStatusColor(value)} />;
      return (
        <BodyCell
          value={icon}
          classes={bodyCellClasses}
          testId={`${CA.RAG}-icon-${getRagStatusTextColor(value)}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.RAG],
    accessor: CA.RAG,
  },
  {
    Header: ({ data }) => {
      const totalParcels =
        columnsConfigs[CA.PARCELS]?.isSubheader &&
        countTotalByField(data, CA.PARCELS);
      return (
        <HeaderCell
          subheader={totalParcels}
          header={PARCELS}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value, rowData }) => {
      const icon = rowData.consolidated && <Consolidated />;
      return (
        <BodyCell
          value={value}
          lastIcon={icon}
          classes={bodyCellClasses}
          extraContainerClasses={[bodyCellClasses.parcelContainer]}
          extraLastIconClasses={[bodyCellClasses.lastIconConsolidatedClass]}
          lastIconTestId={`${CA.PARCELS}-icon-consolidated`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.PARCELS],
    accessor: CA.PARCELS,
  },
  {
    Header: ({ data }) => {
      const totalCadParcels =
        columnsConfigs[CA.CAD]?.isSubheader && countTotalByField(data, CA.CAD);

      return (
        <HeaderCell
          subheader={totalCadParcels}
          header={CAD}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.CAD],
    accessor: CA.CAD,
  },
  {
    Header: ({ data }) => {
      const totalSmalls =
        columnsConfigs[CA.SMALLS]?.isSubheader &&
        countTotalByField(data, CA.SMALLS);
      return (
        <HeaderCell
          subheader={totalSmalls}
          header={SMALLS}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.SMALLS],
    accessor: CA.SMALLS,
  },
  {
    Header: ({ data }) => {
      const totalLoaded = countTotalByField(data, CA.LOADED);
      const subheader = columnsConfigs[CA.LOADED]?.isSubheader && totalLoaded;
      return (
        <HeaderCell
          subheader={subheader}
          header={LOADED}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.LOADED],
    accessor: CA.LOADED,
  },
  {
    Header: () => <HeaderCell header={STOP_TIME} classes={headerCellClasses} />,
    Cell: ({ value, rowData }) => {
      const isPending = isPendingStop(rowData);
      const isEditable = !(
        !modifiers ||
        isPending ||
        isRouteStatusInBuild(route)
      );
      const time = formatStopTime(value || 0);
      const commonProps = {
        value: time,
        classes: bodyCellClasses,
      };
      return !isEditable ? (
        <BodyCell {...commonProps} />
      ) : (
        <EditableCell
          {...commonProps}
          rowData={rowData}
          onSubmit={modifiers?.handleStopTimeChange}
        />
      );
    },
    columnConfig: columnsConfigs[CA.STOP_TIME],
    accessor: CA.STOP_TIME,
  },
  {
    Header: () => <HeaderCell header={ETA} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell value={reformatTime(value, true)} classes={bodyCellClasses} />
    ),
    Footer: () => (
      <BodyCell
        value={formatTimeStringWithDefaultMaxValue(route.routeEndTime)}
        classes={bodyCellClasses}
        extraCellClasses={
          route?.returnToDepotLegalException ? [bodyCellClasses.redText] : []
        }
      />
    ),
    columnConfig: columnsConfigs[CA.ETA],
    accessor: CA.ETA,
  },
  {
    Header: ({ data }) => {
      const totalOpportunityTime =
        columnsConfigs[CA.OT]?.isSubheader && countTotalByField(data, CA.OT);

      return (
        <HeaderCell
          subheader={
            totalOpportunityTime && formatSecondsToHHMM(totalOpportunityTime)
          }
          header={OT}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => {
      const time = !!value && formatSecondsToHHMM(value);
      return <BodyCell value={time} classes={bodyCellClasses} />;
    },
    Footer: () => (
      <BodyCell
        value={
          !!route.opportunityTime && formatSecondsToHHMM(route.opportunityTime)
        }
        classes={bodyCellClasses}
        extraCellClasses={
          route?.returnToDepotLegalException ? [bodyCellClasses.redText] : []
        }
      />
    ),
    columnConfig: columnsConfigs[CA.OT],
    accessor: CA.OT,
  },
  {
    Header: ({ data }) => {
      const totalExceptions =
        columnsConfigs[CA.EXCEPTION]?.isSubheader && totalRouteExceptions(data);
      return (
        <HeaderCell
          subheader={totalExceptions}
          header={EXCEPTION}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value, rowData }) => {
      const exception =
        value && isServiceException({ exception: value })
          ? (!ignoreServiceException(rowData) && getExceptionEntry(value)) || ''
          : getExceptionEntry(value);
      return <BodyCell value={exception} classes={bodyCellClasses} />;
    },
    columnConfig: columnsConfigs[CA.EXCEPTION],
    accessor: CA.EXCEPTION,
  },
  {
    Header: () => <HeaderCell header={WINDOW} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.WINDOW],
    accessor: CA.WINDOW,
  },
  {
    Header: ({ data }) => {
      const totalNonCompatibles =
        columnsConfigs[CA.NC]?.isSubheader &&
        data.reduce(
          (total, row) => (row.nonCompatibles ? total + 1 : total),
          0
        );
      return (
        <HeaderCell
          subheader={totalNonCompatibles}
          header={NON_COMPATIBLES_SHORT}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.NC],
    accessor: CA.NC,
  },
  {
    Header: ({ data }) => {
      const totalWeight =
        columnsConfigs[CA.WEIGHT]?.isSubheader &&
        countTotalByField(data, CA.WEIGHT);
      return (
        <HeaderCell
          subheader={formatToKg(totalWeight)}
          header={WEIGHT}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => (
      <BodyCell
        value={!!value && formatToKg(value)}
        classes={bodyCellClasses}
      />
    ),
    columnConfig: columnsConfigs[CA.WEIGHT],
    accessor: CA.WEIGHT,
  },
  {
    Header: () => <HeaderCell header={CHUTE} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.CHUTE],
    accessor: CA.CHUTE,
  },
  {
    Header: ({ data }) => {
      const totalRequests =
        columnsConfigs[CA.REQUESTS]?.isSubheader &&
        data.reduce(
          (total, row) => (row.requests ? total + row.requests.length : total),
          0
        );
      return (
        <HeaderCell
          subheader={totalRequests}
          header={REQUEST}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value, rowData }) => {
      const onClick = useCallback(
        (e) => {
          e.stopPropagation();
          modifiers.onSelectStopRequests(rowData);
        },
        [rowData]
      );

      if (!value?.length && !rowData.routeOutstandingRequest?.length) {
        return '';
      }

      const { type, count, allowProcessing } = getStopRequestType(value);
      const bodyProps = {
        onClick,
        ...(!!rowData.routeOutstandingRequest?.length && {
          lastIcon: (
            <WarningWithTooltip routes={rowData.routeOutstandingRequest} />
          ),
          lastIconTestId: `${CA.REQUESTS}-icon-warning`,
        }),
      };

      switch (type) {
        case requestIconType.MIXED:
          bodyProps.firstIcon = <MixedAddRemove />;
          bodyProps.onClick = null;
          break;
        case requestIconType.ADD:
          bodyProps.firstIcon = <Add />;
          break;
        case requestIconType.REMOVE:
          bodyProps.firstIcon = <Remove />;
          break;
        case requestIconType.DEFFER:
          bodyProps.firstIcon = <Defer />;
          bodyProps.onClick = allowProcessing ? onClick : null;
          break;
        default:
          bodyProps.onClick = null;
          break;
      }

      if (!bodyProps.firstIcon && !bodyProps.lastIcon) {
        return '';
      }

      return (
        <BodyCell
          value={count}
          classes={bodyCellClasses}
          {...bodyProps}
          firstIconTestId={`${CA.REQUESTS}-icon-${type}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.REQUESTS],
    accessor: CA.REQUESTS,
  },
];

export default stopListColumns;
